import { Component, Inject, ViewChild, inject, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';

import { punktLabel, istPunktartMitProtokoll, idOf, AufnahmePunktDocument } from '../../data-model';
import { ValidationLevel as ValRes, StorageService, SelectionService, BaseListPanelComponent, FILTER_PROVIDER } from '@luzula/luzula-lib';
import { DeleteConfirmationService } from '../../shared/delete-confirmation/delete-confirmation.service';
import { FilterService } from 'src/app/filter.service';
import { AuthService } from 'src/app/auth.service';
import { DataService } from 'src/app/data.service';
import { TransformationService, ProjectPointsService, WdFilterComponent as FilterComponent } from '@luzula/luzula-lib';
import { ToastrService } from 'ngx-toastr';

@Component({
	standalone: false,
	selector: 'bp-bohrpunkt-list-panel',
	templateUrl: './bohrpunkt-list-panel.component.html',
	styleUrls: ['./bohrpunkt-list-panel.component.css'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class BohrpunktListPanelComponent extends BaseListPanelComponent {
	private router = inject(Router);

	@ViewChild('statsHelpModal') statsHelpModal: any;
	@ViewChild('settingsModal') settingsModal: any;

	punktLabel = punktLabel;
	idOf = idOf;
	istPunktartMitProtokoll = istPunktartMitProtokoll;

	constructor(
		public aus: AuthService,
		@Inject(FILTER_PROVIDER) fs: FilterService,
		selectionService: SelectionService,
		private deleteConfirmation: DeleteConfirmationService,
		private ds: DataService,
		storage: StorageService,
		private ts: TransformationService,
		pps: ProjectPointsService,
		private msg: ToastrService
	) {
		super(selectionService, fs, storage, pps);
	}

	// --- Implementierung der abstrakten Methoden ---

	getItemId(bp: any): string {
		return bp._id;
	}

	protected hasProtocol(bp: any): boolean {
		return istPunktartMitProtokoll(bp.type);
	}

	protected matchesSearch(bp: any): boolean {
		if (!this.searchText || this.searchText.trim() === '') return true;

		const searchTerms = this.searchText.trim().split(/\s+/).filter(t => t.length > 0);
		if (searchTerms.length === 0) return true;

		const label = String(this.punktLabel(bp, true) || '').toLowerCase();
		const nummer = String(bp.header?.nummer || '').toLowerCase();
		const forstamt = String(bp.header?.forstamt || '').toLowerCase();
		const revier = bp.header?.revier?.toLowerCase() || '';
		const kartierer = bp.header?.aufnahme?.kartierer?.toLowerCase() || '';
		const type = bp.type?.toLowerCase() || '';
		const datumRaw = bp.header?.aufnahme?.datum || '';
		const jahr = datumRaw ? new Date(datumRaw).getFullYear().toString() : '';
		const vegetation = bp.header?.vegetationsformengruppe?.toLowerCase() || '';

		const bfArray = bp.header?.bodenform?.bf || [];
		const fbfValues = bfArray.map((b: any) => b.fbf || '').join(' ');
		const hbfValues = bfArray.map((b: any) => b.hbf || '').join(' ');
		const hf = bp.header?.humusform?.aufnahmeFlaeche?.neu || bp.header?.humusform?.aufnahmeFlaeche?.alt || '';

		return searchTerms.every(term => {
			const termLower = term.toLowerCase();
			const matchesInsensitive =
				label.includes(termLower) ||
				nummer.includes(termLower) ||
				forstamt.includes(termLower) ||
				revier.includes(termLower) ||
				kartierer.includes(termLower) ||
				type.includes(termLower) ||
				jahr.includes(termLower) ||
				vegetation.includes(termLower);
			const matchesSensitive =
				fbfValues.includes(term) ||
				hbfValues.includes(term) ||
				hf.includes(term);
			return matchesInsensitive || matchesSensitive;
		});
	}

	protected compareBps(a: any, b: any, sortOrder: string): number {
		switch (sortOrder) {
			case 'type':
				return idOf(a.type) - idOf(b.type);
			case 'nummer':
				const numA = a.header?.nummer || '';
				const numB = b.header?.nummer || '';
				return numA.localeCompare(numB, 'de', { numeric: true });
			case 'kartierer':
				const kartA = a.header?.aufnahme?.kartierer || '';
				const kartB = b.header?.aufnahme?.kartierer || '';
				return kartA.localeCompare(kartB, 'de');
			case 'datum':
				const datumA = a.header?.aufnahme?.datum || '';
				const datumB = b.header?.aufnahme?.datum || '';
				return datumB.localeCompare(datumA); // Neueste zuerst
			default:
				return 0;
		}
	}

	// --- App-spezifische Methoden ---

	applyQuickFilter(name: string) {
		this.currentFilterName = name;
		this.storage.setSync('currentFilter', name);

		this.fs.get(name).then((conditions) => {
			if (conditions) {
				if (this.listFilterMode === 'map' && this.currentBounds) {
					const sw = this.currentBounds.getSouthWest();
					const ne = this.currentBounds.getNorthEast();
					const minP = this.ts.toEPSG5650(sw);
					const maxP = this.ts.toEPSG5650(ne);
					const bnds = [{ l: minP, u: maxP }];
					this.fs.storeViews(name, conditions, true, bnds, () => {
						this.fs.applyFilter(name);
						this.mapFilterStale = false;
						this.filterApplied.emit(name);
					});
				} else if (this.listFilterMode === 'selection') {
					this.fs.storeViews(name, conditions, false, [], () => {
						this.fs.applyFilter(name).then(() => {
							const selection = this.selectionService.getSelection();
							const filtered = new Set<string>();
							this.fs.filteredDocIds.forEach(id => {
								if (selection.has(id)) {
									filtered.add(id);
								}
							});
							this.fs.filteredDocIds.clear();
							filtered.forEach(id => this.fs.filteredDocIds.add(id));
							this.updateFilteredCount();
							this.filterApplied.emit(name);
						});
					});
				} else {
					this.fs.storeViews(name, conditions, false, [], () => {
						this.fs.applyFilter(name);
						this.filterApplied.emit(name);
					});
				}
			} else {
				this.currentFilterName = null;
				this.storage.removeSync('currentFilter');
			}
		});
	}

	openFilterEditor() {
		const modalRef = this.modalService.open(FilterComponent, { size: 'lg' });
		modalRef.componentInstance.local = true;
		modalRef.componentInstance.filterRect = this.currentBounds;

		modalRef.componentInstance.filterApplied.subscribe((filterName: string) => {
			this.currentFilterName = filterName;
			this.fs.get(filterName).then((conditions) => {
				if (conditions) {
					this.fs.storeViews(filterName, conditions, false, [], () => this.fs.applyFilter(filterName));
					this.filterApplied.emit(filterName);
				}
			});
		});
	}

	async deleteSelection() {
		const count = this.selectionService.count();
		if (count === 0) {
			this.msg.info('Keine Punkte ausgewählt');
			return;
		}

		// Sammle die ausgewählten Punkte für die erweiterte Bestätigung
		const selectedPoints = this.bps.filter(bp => this.selectionService.isSelected(bp._id));

		const confirmed = await this.deleteConfirmation.confirmDeleteBulkFast(selectedPoints);
		if (!confirmed) return;

		const ids = this.selectionService.getSelectionArray();
		await this.ds.removeBohrpunkteBulk(ids);
		this.selectionService.clear();
		this.pointsDeleted.emit();
	}

	openSettings() {
		this.modalService.open(this.settingsModal, { centered: true });
	}

	showStatsHelp() {
		this.modalService.open(this.statsHelpModal, { size: 'sm', centered: true });
	}

	// Hilfsmethoden für die Listendarstellung
	getSymbolNummer(bp: AufnahmePunktDocument): number {
		return idOf(bp.type);
	}

	getTypName(bp: AufnahmePunktDocument): string {
		return bp.type?.substring(bp.type.indexOf('_') + 1) || '';
	}

	getKartierer(bp: AufnahmePunktDocument): string {
		return bp.header?.aufnahme?.kartierer || '';
	}

	getKurzinfoRed(bp: AufnahmePunktDocument): string {
		let result = '';
		const prefix = bp.header?.bodenform?.prefix;
		if (prefix) result += prefix;
		const bf = bp.header?.bodenform?.bf;
		if (bf && bf.length > 0 && bf[0].fbf) {
			result += bf[0].fbf;
		}
		const ws2 = bp.header?.wasserstufe2;
		const ws = bp.header?.wasserstufe;
		if (ws2) result += ws2;
		if (ws) result += ws;
		return result;
	}

	getKurzinfoGreen(bp: AufnahmePunktDocument): string {
		return bp.header?.humusform?.aufnahmeFlaeche?.neu || bp.header?.humusform?.aufnahmeFlaeche?.alt || '';
	}

	getSvgUrl(bp: AufnahmePunktDocument): string {
		return `assets/img/points/${bp.type}.svg`;
	}

	openProtokoll(event: MouseEvent, bp: AufnahmePunktDocument) {
		event.stopImmediatePropagation();
		event.preventDefault();
		this.router.navigateByUrl('/bohrpunkte/' + bp._id);
	}

	getCommentClass(bp: any): string {
		const severity = bp.state?.commentsSeverity;
		if (severity === ValRes.danger) return 'comment-danger';
		if (severity === ValRes.warning) return 'comment-warning';
		return '';
	}
}
